import { Form } from "antd";
import { useState } from "react";
import { useAuth } from "hooks/useAuth";
import { useUserContext } from "contexts/UserContext";
import { useIntl } from "react-intl";

import { LoginFormValues, UseLoginFormReturnType } from "./types";
import { getGetSecondsUntilNextloginAttempt } from "./utils";

export const useLoginForm = (): UseLoginFormReturnType => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<0 | 1>(0);
  const [secondsUntilNextloginAttempt, setSecondsUntilNextloginAttempt] =
    useState(0);
  const { signIn, answerChallenge } = useAuth();
  const { refetch } = useUserContext();
  const [form] = Form.useForm();

  const onFinish = async (values: LoginFormValues) => {
    try {
      setLoading(true);

      const { phoneNumber, pinCode } = values;

      switch (step) {
        case 0: {
          const user = await signIn(phoneNumber);

          setSecondsUntilNextloginAttempt(
            getGetSecondsUntilNextloginAttempt(user)
          );

          setStep(1);

          break;
        }
        case 1: {
          await answerChallenge(pinCode);
          await refetch();

          break;
        }
        default: {
          throw new Error("Unhandled");
        }
      }
    } catch {
      form.setFields(
        (() => {
          switch (step) {
            case 0: {
              return [
                {
                  name: "phoneNumber",
                  errors: [
                    intl.formatMessage({
                      defaultMessage:
                        "User does not exits or login rate limit has been reached. Please try again.",
                    }),
                  ],
                },
              ];
            }
            case 1: {
              return [
                {
                  name: "pinCode",
                  errors: [
                    intl.formatMessage({
                      defaultMessage: "Verification code is invalid.",
                    }),
                  ],
                },
              ];
            }
            default:
              return [];
          }
        })()
      );
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    try {
      setLoading(true);

      const user = await signIn(form.getFieldValue("phoneNumber"));

      setSecondsUntilNextloginAttempt(getGetSecondsUntilNextloginAttempt(user));
    } catch (error) {
      form.setFields([
        {
          name: "pinCode",
          errors: [
            intl.formatMessage({
              defaultMessage: "Could not resend the code. Please try again.",
            }),
          ],
        },
      ]);

      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    form,
    onFinish,
    resendCode,
    loading,
    step,
    secondsUntilNextloginAttempt,
  };
};
